import fs from "fs";
import path from "path";
import React from "react";
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import Layout from "@swiggy-private/generic-mxn-ui/dist/Layout";
import { IMxNData } from "@swiggy-private/generic-mxn-ui/dist/types";
import type { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { Container, Main } from "../styles/home";
import { validate } from "../utils/validations/ajv";

const FS_FOLDER = "../../../mxn-assets";

export const getStaticPaths = async () => {
    const getFiles = async (dir: string): Promise<string[]> => {
        const dirents = await fs.promises.readdir(dir, { withFileTypes: true });
        const files = await Promise.all(
            dirents.map(async (dirent) => {
                const res = path.resolve(dir, dirent.name);
                return dirent.isDirectory() ? getFiles(res) : [res];
            }),
        );
        return files.flat();
    };
    const paths: {
        params: { name: string[] };
    }[] = [];
    const files = await getFiles(path.resolve(__dirname, FS_FOLDER));
    files.forEach((file: string) => {
        if (file.includes(".json") && !file.includes("mxn-assets/collections/")) {
            const fileName = file.split("mxn-assets/")[1].split(".")[0];

            paths.push({
                params: { name: fileName.split("/") },
            });
        }
    });

    return {
        paths,
        fallback: false,
    };
};

export const getStaticProps = async (context: { params: { name: string[] } }) => {
    const fileName = context.params.name.join("/");
    const rawdata = fs.readFileSync(path.resolve(__dirname, FS_FOLDER + "/" + fileName + ".json"));
    const data = JSON.parse(rawdata.toString());
    if (validate(data)) {
        return {
            props: { data: data, slugPath: fileName },
        };
    } else {
        console.error(fileName, validate.errors);
        throw new Error("wrong schema");
    }
};

const MxNPage: NextPage<{ data: IMxNData }> = ({ data }: { data: IMxNData }) => {
    const router = useRouter();

    const title = data.containerMeta?.pageDetails?.title || "Welcome to Swiggy";
    const description = data.containerMeta?.pageDetails?.description || "Welcome to Swiggy";
    const icon = getCloudinaryUrl(data.containerMeta?.pageDetails?.icon || "portal/m/logo_192x192.png", {
        width: 72,
        height: 72,
    });
    const metaProperties = data.containerMeta?.pageDetails?.meta?.filter((meta) => meta.type === "property") || [];
    const preloadProperties =
        data.containerMeta?.pageDetails?.preload?.filter((preload) => preload.type === "property") || [];

    return (
        <Container>
            <Head>
                <title>{title}</title>
                <meta httpEquiv="Content-type" content="text/html; charset=utf-8" />
                <meta charSet="utf-8" />
                <meta name="description" content={description} />
                <meta
                    name="viewport"
                    content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
                />
                {metaProperties.map((metaProp) => (
                    <meta property={metaProp.key} content={metaProp.value} key={metaProp.key} />
                ))}
                <link rel="preconnect" href={process.env.NEXT_PUBLIC_CDN_URL} crossOrigin="anonymous" />
                <link rel="dns-prefetch" href={process.env.NEXT_PUBLIC_CDN_URL} />
                <link rel="icon" href={icon} />
                {preloadProperties.map((preloadProp, index) => (
                    <link
                        rel="preload"
                        href={preloadProp.href}
                        as={preloadProp.as}
                        key={index}
                        crossOrigin="anonymous"
                    />
                ))}
            </Head>

            <Main>
                {router.isReady || data.hasStaticParamsOnly ? (
                    <Layout query={router.query as Record<string, string>} data={data} />
                ) : (
                    <></>
                )}
            </Main>
        </Container>
    );
};

export default MxNPage;
